import React, { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Loader2, Settings2, Upload } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 2 * 1024 * 1024;

function StdVideoSection({ settings, onRefresh }) {
  const [pct, setPct] = useState(null);
  const ref = useRef(null);
  const sv = settings.std_video;
  const mb = (b) => (b ? `${(b / 1e6).toFixed(1)} MB` : '—');

  useEffect(() => {
    if (sv?.status !== 'processing') return;
    const t = setInterval(onRefresh, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sv?.status]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error('Video too large (max 200MB)'); return; }
    setPct(0);
    try {
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const init = await api.initChunkedProject(file.name, totalChunks);
      for (let i = 0; i < totalChunks; i++) {
        await api.uploadChunk(init.data.upload_id, i, file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        setPct(Math.round(((i + 1) / totalChunks) * 95));
      }
      await api.completeStandardVideo(init.data.upload_id);
      setPct(null);
      onRefresh();
      toast.success('Standard video uploaded — HD & compact versions are being created');
    } catch (e) { setPct(null); toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  return (
    <div className="border-t border-border/60 pt-3 space-y-2" data-testid="std-video-section">
      <p className="text-[10px] uppercase tracking-wider text-sky-400">Standard video (gold standard)</p>
      {sv?.original_key ? (
        <div className="space-y-1.5 text-[11px]">
          <p className="text-foreground font-mono truncate" data-testid="std-video-filename">
            {sv.original_filename} · {mb(sv.original_size)}
          </p>
          {sv.status === 'processing' && (
            <p className="text-amber-400 flex items-center gap-1.5" data-testid="std-video-processing">
              <Loader2 className="w-3 h-3 animate-spin" /> Creating HD &amp; compact versions…
            </p>
          )}
          {sv.status === 'failed' && <p className="text-red-400">Compression failed — try a different file.</p>}
          {sv.status === 'ready' && (
            <div className="flex flex-wrap gap-x-3 gap-y-1" data-testid="std-video-versions">
              <a className="text-sky-400 hover:text-sky-300" href={`${API_BASE}/api/landing/standard-video/download?version=hd`} data-testid="std-video-dl-hd">
                HD 1080p · {mb(sv.hd?.size)}
              </a>
              <a className="text-sky-400 hover:text-sky-300" href={`${API_BASE}/api/landing/standard-video/download?version=compact`} data-testid="std-video-dl-compact">
                Compact 720p · {mb(sv.compact?.size)}
              </a>
              <a className="text-muted-foreground hover:text-foreground" href={`${API_BASE}/api/landing/standard-video/download?version=original`} data-testid="std-video-dl-original">
                Original · {mb(sv.original_size)}
              </a>
            </div>
          )}
          {sv.status === 'ready' && (
            <p className="text-[10px] text-muted-foreground">Use it on any landing page via "Use standard video" in the video section.</p>
          )}
        </div>
      ) : (
        <p className="text-[10px] text-muted-foreground">
          Upload your gold-standard video once — every landing page can then use it with one click. The original stays untouched; HD (1080p) and compact (720p) versions are generated automatically.
        </p>
      )}
      <input ref={ref} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden"
        onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} data-testid="std-video-file" />
      {pct !== null ? (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-sky-600 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] font-mono text-muted-foreground">{pct}%</span>
        </div>
      ) : (
        <Button onClick={() => ref.current?.click()} variant="outline" data-testid="std-video-upload-btn"
          className="border-border text-muted-foreground hover:text-foreground rounded-sm text-[11px] h-7 px-3">
          <Upload className="w-3 h-3 mr-1" /> {sv?.original_key ? 'Replace standard video' : 'Upload standard video'}
        </Button>
      )}
    </div>
  );
}

export const LandingSettingsDialog = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [voices, setVoices] = useState([]);
  const [connections, setConnections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [applyingAll, setApplyingAll] = useState(false);
  const [photoTs, setPhotoTs] = useState(Date.now());
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.getLandingSettings().then(r => setSettings(r.data)).catch(() => {});
    api.getLandingVoices().then(r => setVoices(r.data.voices || [])).catch(() => {});
    api.getMsConnections().then(r => setConnections(r.data.connections || [])).catch(() => {});
  }, [open]);

  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateLandingSettings({
        sender_name: settings.sender_name,
        sender_full_name: settings.sender_full_name,
        phone: settings.phone,
        email: settings.email,
        website_url: settings.website_url,
        voice_id: settings.voice_id,
        spoken_template: settings.spoken_template,
        calendar_user_id: settings.calendar_user_id || null,
        invite_emails: (typeof settings.invite_emails === 'string'
          ? settings.invite_emails.split(',')
          : settings.invite_emails || []).map(e => e.trim()).filter(Boolean),
        appointment_duration: settings.appointment_duration,
      });
      toast.success('Landing settings saved');
      setOpen(false);
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleApplyAll = async () => {
    if (!window.confirm('Overwrite the spoken text of ALL existing landing pages with the current template? Manual edits will be lost. Existing audio stays until you regenerate it.')) return;
    setApplyingAll(true);
    try {
      await api.updateLandingSettings({ spoken_template: settings.spoken_template });
      const r = await api.rerenderAllLandings();
      toast.success(`Template applied to ${r.data.updated} landing page${r.data.updated !== 1 ? 's' : ''}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply template'); }
    setApplyingAll(false);
  };

  const handlePhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.uploadLandingPhoto(fd);
      setPhotoTs(Date.now());
      setSettings(s => ({ ...s, photo_key: 'set' }));
      toast.success('Photo updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Photo upload failed'); }
  };

  const inputCls = 'w-full bg-secondary/40 border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:border-sky-700';

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="ghost" data-testid="landing-settings-btn"
        className="border border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-9 px-3">
        <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Landing settings
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-md max-h-[85vh] overflow-y-auto" data-testid="landing-settings-dialog">
          <DialogHeader><DialogTitle className="text-foreground text-base">Landing page settings</DialogTitle></DialogHeader>
          {!settings ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-secondary border border-border shrink-0">
                  {settings.photo_key ? (
                    <img src={`${API_BASE}/api/landing/settings/photo?t=${photoTs}`} alt="" className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-lg">?</div>
                  )}
                </div>
                <div>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                    onChange={e => handlePhoto(e.target.files?.[0])} />
                  <Button onClick={() => fileRef.current?.click()} variant="outline" data-testid="landing-photo-upload"
                    className="border-border text-muted-foreground hover:text-foreground rounded-sm text-[11px] h-7 px-3">
                    <Upload className="w-3 h-3 mr-1" /> Sender photo
                  </Button>
                  <p className="text-[10px] text-muted-foreground mt-1">Shown in the circle on every landing page</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">First name</label>
                  <input value={settings.sender_name || ''} onChange={e => set('sender_name', e.target.value)} className={inputCls} data-testid="landing-sender-name" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Full name</label>
                  <input value={settings.sender_full_name || ''} onChange={e => set('sender_full_name', e.target.value)} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Phone</label>
                <input value={settings.phone || ''} onChange={e => set('phone', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</label>
                <input value={settings.email || ''} onChange={e => set('email', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Website URL (shown prominently on the landing page)</label>
                <input value={settings.website_url || ''} onChange={e => set('website_url', e.target.value)} className={inputCls} data-testid="landing-website-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">ElevenLabs voice</label>
                <select value={settings.voice_id || ''} onChange={e => set('voice_id', e.target.value)} className={inputCls} data-testid="landing-voice-select">
                  {voices.length === 0 && <option value={settings.voice_id}>Loading voices…</option>}
                  {voices.map(v => (
                    <option key={v.voice_id} value={v.voice_id}>{v.name}{v.category === 'cloned' ? ' (your clone)' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Spoken text template</label>
                <textarea value={settings.spoken_template || ''} onChange={e => set('spoken_template', e.target.value)}
                  className="w-full min-h-[140px] bg-secondary/40 border border-border rounded-sm px-3 py-2 text-[11px] text-foreground leading-relaxed resize-y focus:outline-none focus:border-sky-700"
                  data-testid="landing-spoken-template" />
                <p className="text-[9px] text-muted-foreground mt-1">
                  Placeholders: <span className="font-mono">[NACHNAME]</span>, <span className="font-mono">[FIRMENNAME]</span>, <span className="font-mono">[ORT]</span>, <span className="font-mono">[INDIVIDUELLER SATZ …]</span> (auto-filled by AI per company). Applied to newly created landing pages — existing pages keep their text.
                </p>
                <Button onClick={handleApplyAll} disabled={applyingAll} variant="outline" data-testid="landing-apply-template-all"
                  className="mt-2 border-amber-900/50 text-amber-400 hover:text-amber-300 rounded-sm text-[11px] h-7 px-3">
                  {applyingAll ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                  Apply template to ALL existing pages
                </Button>
              </div>
              <div className="border-t border-border/60 pt-3 space-y-3">
                <p className="text-[10px] uppercase tracking-wider text-sky-400">Appointment booking (Outlook calendar)</p>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Calendar owner</label>
                  <select value={settings.calendar_user_id || ''} onChange={e => set('calendar_user_id', e.target.value)}
                    className={inputCls} data-testid="landing-calendar-select">
                    <option value="">Auto (Max, if connected)</option>
                    {connections.map(c => (
                      <option key={c.user_id} value={c.user_id}>
                        {c.name} — {c.account}{!c.calendar_ready ? ' (reconnect needed)' : ''}
                      </option>
                    ))}
                  </select>
                  {connections.length === 0 && (
                    <p className="text-[10px] text-amber-400 mt-1">No Microsoft account connected yet — connect in My Settings → Microsoft To Do (grants calendar access too).</p>
                  )}
                  {connections.some(c => !c.calendar_ready) && (
                    <p className="text-[10px] text-amber-400 mt-1">Accounts marked "reconnect needed" must disconnect &amp; reconnect Microsoft once to grant the new calendar permission.</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Duration</label>
                    <select value={settings.appointment_duration || 45} onChange={e => set('appointment_duration', parseInt(e.target.value))}
                      className={inputCls} data-testid="landing-duration-select">
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>60 min</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Invite (comma-sep.)</label>
                    <input
                      value={typeof settings.invite_emails === 'string' ? settings.invite_emails : (settings.invite_emails || []).join(', ')}
                      onChange={e => set('invite_emails', e.target.value)}
                      placeholder="kontakt@olta-nachfolge.com"
                      className={inputCls} data-testid="landing-invite-emails" />
                  </div>
                </div>
              </div>
              <StdVideoSection settings={settings}
                onRefresh={() => api.getLandingSettings().then(r => setSettings(r.data)).catch(() => {})} />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setOpen(false)} className="text-xs h-8">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} data-testid="landing-settings-save"
                  className="bg-sky-700 hover:bg-sky-600 text-white rounded-sm text-xs h-8 px-4">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
